import {
    AfterContentInit,
    Directive,
    ElementRef,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    Optional,
    Self
} from '@angular/core';
import {DefaultValueAccessor, NgControl} from '@angular/forms';
import {Subscription} from 'rxjs';
import {FormValueSyncRegistry, SyncableInput} from './form-value-sync.registry';

/** Keyframe name declared in styles/_autofill-hook.scss. */
const AUTOFILL_ANIMATION_NAME = 'edoc-autofill-start';

/**
 * Input types holding free text. Password is left out on purpose: a trailing
 * space can be part of a password.
 */
const TRIMMABLE_TYPES = new Set(['text', 'email', 'search', 'tel', 'url', 'textarea']);

/**
 * Keeps a form control in step with the DOM value of its input, whatever put
 * that value there.
 *
 * Angular's DefaultValueAccessor takes the value from the `input` event alone,
 * and even then discards it while an IME composition is open. Two independent
 * ways to lose text follow from that, and both end the same way — the browser
 * visibly shows text in the box, the control is still null, and the payload is
 * built from control values, so the value is silently dropped:
 *
 *  - a fill mechanism writes `input.value` without dispatching `input` at all
 *    (password managers, autofill extensions, in-app browser webviews);
 *  - the text is composed rather than typed, which on iOS covers dictation,
 *    QuickType suggestions and autocorrect — see onCompositionEnd below.
 *
 * This directive reconciles DOM -> control on every event that could indicate a
 * fill, and registers itself so FormValueSyncRegistry.syncAll() can sweep every
 * input immediately before a form is read. It also normalises '' to null, which
 * is the behaviour the former NullOnEmptyDirective provided.
 *
 * Text values are trimmed when the user leaves the field or presses Enter, and
 * when they're set from code (e.g. a form populated from the API) while the
 * field isn't focused. A whitespace-only value becomes null. Never while typing:
 * rewriting the value mid-entry moves the caret to the end, so a space between
 * two words is lost.
 *
 * Applied automatically by its selector — importing it into a standalone
 * component's `imports` is all that is needed.
 */
@Directive({
    standalone: true,
    selector: 'input[formControl], input[formControlName], textarea[formControl], textarea[formControlName]'
})
export class FormValueSyncDirective implements OnInit, AfterContentInit, OnDestroy, SyncableInput {
    private readonly _element = inject<ElementRef<HTMLInputElement | HTMLTextAreaElement>>(ElementRef);
    private readonly _registry = inject(FormValueSyncRegistry);

    /**
     * Only inputs driven by Angular's own text accessor are safe to reconcile.
     * A custom ControlValueAccessor keeps a model value that deliberately
     * differs from what is rendered — ngx-mask holds unmasked digits while the
     * box shows "4111 1111 ____", so copying the DOM value across would corrupt
     * the card number. The same guard excludes checkboxes, radios and numbers.
     */
    private _syncable = false;
    private _valueChanges?: Subscription;

    constructor(@Optional() @Self() private _ngControl: NgControl | null) {}

    ngOnInit(): void {
        this._syncable = this._ngControl?.valueAccessor instanceof DefaultValueAccessor;

        if (this._syncable) {
            this._registry.register(this);

            // Covers forms populated before this field was rendered, then every later patch.
            this.trimPatched();
            this._valueChanges = this._ngControl?.valueChanges?.subscribe(() => this.trimPatched());
        }
    }

    /**
     * Not in ngOnInit: FormControlName creates its control in its own ngOnChanges,
     * which may run after this directive's ngOnInit depending on import order. By
     * AfterContentInit every directive on the element is initialised.
     */
    ngAfterContentInit(): void {
        if (!this._syncable) return;

        // Covers forms populated before this field was rendered, then every later patch.
        this.trimPatched();
        this._valueChanges = this._ngControl?.valueChanges?.subscribe(() => this.trimPatched());
    }

    ngOnDestroy(): void {
        this._registry.unregister(this);
        this._valueChanges?.unsubscribe();
    }

    /**
     * Adopts the DOM value when it differs from the control value. Safe to call
     * repeatedly — it is a no-op once the two agree.
     */
    sync(): void {
        if (!this._syncable) return;

        const control = this._ngControl?.control;
        if (!control || control.disabled) return;

        // A control holding anything other than text is driven by something
        // that formats its own display value; never overwrite it with markup.
        if (control.value != null && typeof control.value !== 'string') return;

        const raw = this._element.nativeElement.value;
        const next = raw === '' ? null : raw;

        if (next === control.value) return;

        // emitModelToViewChange: false stops Angular writing the value straight
        // back into the element it was just read from, which would reset the
        // caret while the user is typing.
        control.setValue(next, {emitModelToViewChange: false});
        control.markAsDirty();
    }

    private trim(): void {
        const element = this._element.nativeElement;
        if (!this._syncable || !TRIMMABLE_TYPES.has(element.type)) return;

        const control = this._ngControl?.control;
        if (!control || control.disabled || typeof control.value !== 'string') return;

        const value = control.value;
        const trimmed = value.trim();
        if (trimmed === value) return;

        control.setValue(trimmed.length === 0 ? null : trimmed);
        control.markAsDirty();
    }

    /**
     * Trims a value set from code without marking the form dirty: loading a record
     * must not flag it as changed. Skipped while the field has focus, since that
     * change is the user typing.
     */
    private trimPatched(): void {
        const element = this._element.nativeElement;
        if (!this._syncable || !TRIMMABLE_TYPES.has(element.type) || element === document.activeElement) return;

        const control = this._ngControl?.control;
        const value = control?.value;
        if (!control || typeof value !== 'string') return;

        const trimmed = value.trim();
        if (trimmed === value) return;

        control.setValue(trimmed.length === 0 ? null : trimmed, {emitEvent: false});
    }

    @HostListener('input')
    onInput(): void {
        this.sync();
    }

    @HostListener('change')
    onChange(): void {
        this.sync();
    }

    @HostListener('blur')
    onBlur(): void {
        this.sync();
        this.trim();
    }

    /**
     * Enter submits a form without blurring the field. Keydown fires before the
     * submit, so the trimmed value is what gets submitted. Textareas are skipped
     * since Enter there inserts a new line.
     */
    @HostListener('keydown.enter')
    onEnter(): void {
        if (this._element.nativeElement.type === 'textarea') return;

        this.sync();
        this.trim();
    }

    /**
     * iOS is the reason this listener exists.
     *
     * DefaultValueAccessor buffers IME input whenever COMPOSITION_BUFFER_MODE is
     * on, and it turns itself on for every non-Android agent — so on iPhone it is
     * always active. While a composition is open it throws `input` events away
     * and only commits on compositionend; its blur handler just marks the control
     * touched. Dictation, QuickType suggestions and autocorrect all compose, so
     * text entered that way exists only in the DOM until the composition closes.
     *
     * Our own `input` listener already bypasses that buffer, but Safari can
     * rewrite the value *at* compositionend (autocorrect swapping a word) without
     * a trailing `input` event. Syncing here commits the corrected text instead of
     * leaving it to the blur/submit fallback.
     */
    @HostListener('compositionend')
    onCompositionEnd(): void {
        this.sync();
    }

    /**
     * Chrome, Safari and Edge match `:-webkit-autofill` the moment they fill a
     * field. The stylesheet hangs a no-op animation off that selector purely so
     * this event fires and gives us a reliable autofill signal.
     */
    @HostListener('animationstart', ['$event'])
    onAnimationStart(event: AnimationEvent): void {
        if (event.animationName === AUTOFILL_ANIMATION_NAME) {
            this.sync();
        }
    }
}
